import pMap from 'p-map';
import IImage, { isImage } from '../../types/data/base/IImage';
import ImageSrcset from '../../types/data/base/IImageSrcset';
import IVideo, { isVideo } from '../../types/data/base/IVideo';
import IVideoSource from '../../types/data/base/IVideoSource';

// if other than runtime: needs to match cache name configured in the pwa section of vue.config.js:
export const CACHE_NAME = 'jbm-sales-app-media';

let instance: AssetCacheService | null;

export interface IPromiseResolutionFunctions {
	resolve: Function; // eslint-disable-line @typescript-eslint/ban-types
	reject: Function; // eslint-disable-line @typescript-eslint/ban-types
}

export default class AssetCacheService {
	/**
	 * Access to the cache API instance
	 */
	private assetCachePromise: Promise<Cache> = window.caches.open(CACHE_NAME);

	/**
	 * promise when resolved signals that the current batch of assets has been loaded and a next batch may be added
	 */
	private batchLoadingPromise: Promise<void> = Promise.resolve();

	/**
	 * Promises for all urls requested to be cached.
	 * The same promise may be included by multiple `cacheAssetUrls()` results so that if the promise is resolved,
	 * every "requester" is notified via a resolving promise.
	 * Event urls already cached are included as well once they are requested to be cached once - these promises just resolve momentarily
	 */
	private assetLoadingPromisesByUrl: Record<string, Promise<void>> = {};

	private assetLoadingPromisesResolutionFunctionsByUrl: Record<string, IPromiseResolutionFunctions> = {};

	public static getInstance(): AssetCacheService {
		instance = instance || new AssetCacheService();

		return instance;
	}

	/**
	 * Returns a promise resolving in to the cache instance used to cached assets
	 */
	private async getAssetCache(): Promise<Cache> {
		return this.assetCachePromise;
	}

	/**
	 * Create asset loading promises for all urls that weren't processed yet.
	 * If the url is found in cache, the loading promise will be resolved immediately.
	 *
	 * Returns the subset of urls that are not already being loaded or already exist in cache
	 *
	 * @param urls
	 */
	private async initAssetLoadingPromises(urls: string[]): Promise<string[]> {
		const assetCache: Cache = await this.getAssetCache();

		const newUrls: string[] = [];

		await Promise.all(
			urls
				// only check urls that were not already checked and added to the promise stack
				.filter((url: string): boolean => !this.assetLoadingPromisesByUrl[url])
				.map(async (url: string): Promise<void> => {
					if (!this.assetLoadingPromisesByUrl[url]) {
						// eslint-disable-next-line @typescript-eslint/ban-types
						this.assetLoadingPromisesByUrl[url] = new Promise((resolve: Function, reject: Function): void => {
							this.assetLoadingPromisesResolutionFunctionsByUrl[url] = { resolve, reject };
						});

						// resolve loading promise if url is already cached
						if ((await assetCache.keys(url)).length > 0) {
							this.assetLoadingPromisesResolutionFunctionsByUrl[url].resolve();
						} else {
							newUrls.push(url);
						}
					}
				})
		);

		return newUrls;
	}

	/**
	 * Caches a set of given urls
	 * @param urls
	 */
	public async cacheAssets(urls: string[]): Promise<Record<string, Promise<void>>> {
		const assetCache: Cache = await this.getAssetCache();

		const newUrls: string[] = await this.initAssetLoadingPromises(urls);

		if (newUrls.length > 0) {
			console.info(`[AssetCacheService] ${newUrls.length} new assets to fetch and cache...`);

			this.batchLoadingPromise = this.batchLoadingPromise.then(async (): Promise<void> => {
				await pMap(
					newUrls,
					async (url: string): Promise<void> =>
						assetCache
							.add(url)
							.catch((error: Error): void => {
								console.error(`[AssetCacheService] could not fetch asset with url '${url}'`, error);
								this.assetLoadingPromisesResolutionFunctionsByUrl[url].resolve();
								// remove promise from promise stack so we may restart fetching this asset fresh in a next `startAssetCaching()` run
								// this.assetLoadingPromisesResolutionFunctionsByUrl[url].reject(error);

								// remove promise so fetch may be tried anew the next time the asset is added
								delete this.assetLoadingPromisesByUrl[url];
							})
							.then((): void => {
								this.assetLoadingPromisesResolutionFunctionsByUrl[url].resolve();
							}),
					{
						concurrency: 5
					}
				);
			});
		}

		const assetLoadingPromisesByUrl: Record<string, Promise<void>> = {};

		urls.forEach((url: string): void => {
			assetLoadingPromisesByUrl[url] = this.assetLoadingPromisesByUrl[url];
		});

		return assetLoadingPromisesByUrl;
	}

	/**
	 * reset loading promises
	 */
	public async reset(): Promise<void> {
		this.assetLoadingPromisesByUrl = {};
	}

	/**
	 * Flushes all cached requests
	 */
	public async flush(): Promise<void> {
		await window.caches.delete(CACHE_NAME);
		this.assetCachePromise = window.caches.open(CACHE_NAME);
	}

	/**
	 * Extracts all asset urls from an object or array
	 */
	public extractAssetUrls(value: Record<string, any> | any[]): string[] {
		if (!value) return [];

		const urls: string[] = [];

		if (isImage(value)) {
			const image: IImage = value as IImage;
			if (image.src) urls.push(image.src);
			if (image.srcset) urls.push(...image.srcset.map((srcset: ImageSrcset): string => srcset.src));
		} else if (isVideo(value)) {
			const video: IVideo = value as IVideo;
			if (video.poster) urls.push(video.poster);
			if (video.sources) urls.push(...video.sources.map((source: IVideoSource): string => source.src));
		} else if (Array.isArray(value)) {
			value.forEach((valueEntry: any): number => urls.push(...this.extractAssetUrls(valueEntry)));
		} else if (typeof value === 'object') {
			Object.values(value).forEach((valueEntry: any): number => urls.push(...this.extractAssetUrls(valueEntry)));
		}

		return urls;
	}
}
